import * as React from 'react';
import { ISSOProvider } from '../../../../../data/types';
import { SelectionCard } from '../../../../shared/SelectionCard';

export interface ISSOMethodCardProps {
  provider: ISSOProvider;
  onClick: (provider: ISSOProvider) => void;
  selected: boolean;
  animationDelay?: number;
}

/**
 * Get the appropriate icon for an SSO provider based on its name or type
 */
const getProviderIcon = (provider: ISSOProvider): string => {
  const name = provider.name.toLowerCase();

  // Check for specific provider names
  if (name.includes('google')) {
    return 'cloud';
  } else if (name.includes('microsoft') || name.includes('azure')) {
    return 'cloud';
  } else if (name.includes('okta')) {
    return 'verified_user';
  } else if (provider.type === 'saml') {
    return 'security';
  } else if (provider.type === 'oidc') {
    return 'account_circle';
  }

  // Default icon
  return 'business';
};

/**
 * Get a user-friendly description for an SSO provider
 */
const getProviderDescription = (provider: ISSOProvider): string => {
  if (provider.type === 'saml') {
    return 'Sign in with SAML';
  } else if (provider.type === 'oidc') {
    return 'Sign in with OpenID Connect';
  }
  return 'Sign in with SSO';
};

export const SSOMethodCard = React.memo(
  (props: ISSOMethodCardProps): React.ReactElement => {
    const handleClick = React.useCallback((): void => {
      props.onClick(props.provider);
    }, [props.onClick, props.provider]);

    return (
      <SelectionCard
        selected={props.selected}
        onClick={handleClick}
        icon={getProviderIcon(props.provider)}
        title={props.provider.name}
        description={getProviderDescription(props.provider)}
        animationDelay={props.animationDelay}
      />
    );
  },
  (
    prevProps: Readonly<ISSOMethodCardProps>,
    nextProps: Readonly<ISSOMethodCardProps>
  ): boolean => {
    return (
      prevProps.selected === nextProps.selected &&
      prevProps.provider.id === nextProps.provider.id &&
      prevProps.onClick === nextProps.onClick &&
      prevProps.animationDelay === nextProps.animationDelay
    );
  }
);

SSOMethodCard.displayName = 'SSOMethodCard';
